import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { AudioRecordingSection } from '@components/AudioRecordingSection';
import { InfoBox } from '@components/InfoBox';
import { VOICE_RECORDING_GUIDELINES } from '@constants/voice-recording.constant';
import { useDialog } from '@contexts/DialogContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useVoiceRecordings } from '@contexts/VoiceRecordingsContext';
import { ChooseLanguage } from '@features/voice-recordings/record-voice/components/ChooseLanguage';
import { VoiceSampleActionsFooter } from '@features/voice-recordings/record-voice/components/VoiceSampleActionsFooter';
import { Typography } from '@mui/material';
import { voiceRecordingsService } from '@services/voice-recordings.service';
import { getConsentText, getReferenceScript } from '@utils/language.utils';
import { isAppApiError } from '@utils/service.utils';
import { useCallback, useMemo, useState } from 'react';
export const RecordVoice = () => {
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const { getLocalRecording, addLocalRecording, clearAllLocalRecordings, fetchRemoteRecordings, } = useVoiceRecordings();
    const { showSnackbar } = useSnackbar();
    const { showDialog } = useDialog();
    // Get language-specific content
    const consentText = useMemo(() => getConsentText(selectedLanguage), [selectedLanguage]);
    const referenceScript = useMemo(() => getReferenceScript(selectedLanguage), [selectedLanguage]);
    // Derive recording URLs from context using useMemo
    const { consentRecordingUrl, referenceRecordingUrl } = useMemo(() => {
        if (!selectedLanguage) {
            return { consentRecordingUrl: null, referenceRecordingUrl: null };
        }
        return {
            consentRecordingUrl: getLocalRecording(selectedLanguage, 'consentAudio')?.url || null,
            referenceRecordingUrl: getLocalRecording(selectedLanguage, 'referenceAudio')?.url || null,
        };
    }, [getLocalRecording, selectedLanguage]);
    const handleConsentComplete = useCallback((blob) => {
        addLocalRecording({
            languageKey: selectedLanguage,
            type: 'consentAudio',
            blob,
        });
        showSnackbar('Consent captured successfully!', 'info');
    }, [addLocalRecording, showSnackbar, selectedLanguage]);
    const handleReferenceRecordingComplete = useCallback((blob) => {
        addLocalRecording({
            languageKey: selectedLanguage,
            type: 'referenceAudio',
            blob,
        });
        showSnackbar('Sample voice captured successfully!', 'info');
    }, [addLocalRecording, showSnackbar, selectedLanguage]);
    const handleUploadRecordings = useCallback(async () => {
        if (!selectedLanguage) {
            showSnackbar('Please select a language', 'error');
            return;
        }
        const consentRecording = getLocalRecording(selectedLanguage, 'consentAudio');
        const referenceRecording = getLocalRecording(selectedLanguage, 'referenceAudio');
        if (!consentRecording?.blob || !referenceRecording?.blob) {
            showSnackbar('Both recordings are required', 'error');
            return;
        }
        setIsUploading(true);
        try {
            await voiceRecordingsService.uploadRecordings({
                languageKey: selectedLanguage,
                consentAudio: consentRecording.blob,
                referenceAudio: referenceRecording.blob,
                consentText,
            });
            showDialog('Voice samples uploaded successfully!', {
                onClose: () => {
                    // Clear local recordings for this language after successful upload
                    clearAllLocalRecordings();
                    // Fetch updated remote recordings
                    fetchRemoteRecordings();
                },
            });
        }
        catch (error) {
            const errorMessage = isAppApiError(error)
                ? error.message
                : 'Failed to upload recordings. Please try again.';
            showSnackbar(String(errorMessage), 'error');
        }
        finally {
            setIsUploading(false);
        }
    }, [
        selectedLanguage,
        getLocalRecording,
        clearAllLocalRecordings,
        fetchRemoteRecordings,
        showSnackbar,
        showDialog,
        consentText,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h1", color: "primary", children: "Voice Recording" }), _jsx(InfoBox, { title: "Follow these guidelines for producing high-quality reference and consent audios:", items: VOICE_RECORDING_GUIDELINES }), _jsx(ChooseLanguage, { value: selectedLanguage, onChange: setSelectedLanguage }), _jsx(AudioRecordingSection, { sectionNumber: 1, sectionTitle: "Consent", enabled: Boolean(selectedLanguage), recordingUrl: consentRecordingUrl, maxRecordingDuration: 15, readoutText: consentText, estimatedTime: "5 to 15 seconds", readoutNote: "Voice is used only for Tata Fleet application purpose.", startRecordingLabel: "Record Consent", stopRecordingLabel: "Stop Recording Consent", onRecordingComplete: handleConsentComplete, onError: error => {
                    showSnackbar(error, 'error');
                } }), _jsx(AudioRecordingSection, { sectionNumber: 2, sectionTitle: "Sample Voice Recording", enabled: Boolean(consentRecordingUrl), recordingUrl: referenceRecordingUrl, maxRecordingDuration: 10, readoutText: referenceScript, estimatedTime: "Up to 10 seconds", startRecordingLabel: "Record Sample Voice", stopRecordingLabel: "Stop Recording Sample Voice", onRecordingComplete: handleReferenceRecordingComplete, onError: error => {
                    showSnackbar(error, 'error');
                } }), consentRecordingUrl && referenceRecordingUrl && (_jsx(VoiceSampleActionsFooter, { onRestart: () => clearAllLocalRecordings(), onUpload: handleUploadRecordings, isUploading: isUploading }))] }));
};
